import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

export type RecordCreateAction = "expense" | "income" | "transfer";

const recordCreateActions = new Set<RecordCreateAction>(["expense", "income", "transfer"]);

function parseRecordCreateAction(value: string | null): RecordCreateAction | null {
  return value && recordCreateActions.has(value as RecordCreateAction) ? (value as RecordCreateAction) : null;
}

export function useRecordsUrlAction() {
  const [searchParams, setSearchParams] = useSearchParams();
  const createAction = parseRecordCreateAction(searchParams.get("create"));
  const editAction = createAction === null ? searchParams.get("edit") || null : null;
  const editReturnAction = editAction && searchParams.get("return") === "statistics" ? "statistics" : null;
  const importAction = createAction === null && editAction === null && searchParams.get("import") === "statement";

  const clearCreateAction = useCallback(
    (expectedAction: RecordCreateAction) => {
      setSearchParams(
        (current) => {
          if (parseRecordCreateAction(current.get("create")) !== expectedAction) return current;
          const next = new URLSearchParams(current);
          next.delete("create");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearImportAction = useCallback(() => {
    setSearchParams(
      (current) => {
        if (current.get("import") !== "statement") return current;
        const next = new URLSearchParams(current);
        next.delete("import");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const clearEditAction = useCallback(
    (expectedId: string) => {
      setSearchParams(
        (current) => {
          if (current.get("edit") !== expectedId) return current;
          const next = new URLSearchParams(current);
          next.delete("edit");
          next.delete("return");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { createAction, editAction, editReturnAction, importAction, clearCreateAction, clearEditAction, clearImportAction };
}
